import React from "react";
import { StochasticDashboardState } from "@/modules/stochastic/features/Reports/SCDashboard/slice/StochasticDashboardSlice";
import TotalSalesNewVsExistingCustomerChart from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalSalesNewVsExistingCustomerChart/TotalSalesNewVsExistingCustomerChart";
import TotalSalesNewVsExistingCustomerChartSkeleton from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalSalesNewVsExistingCustomerChart/TotalSalesNewVsExistingCustomerChartSkeleton";
import TotalSalesByYearAndMonthChart from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalSalesByYearAndMonthChart/TotalSalesByYearAndMonthChart";
import { TotalSalesByYearAndMonthChartSkeleton } from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalSalesByYearAndMonthChart/TotalSalesByYearAndMonthChartSkeleton";
import TotalSalesByZipCodeChart from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalSalesByZipCodeChart/TotalSalesByZipCodeChart";
import { TotalSalesByZipCodeChartSkeleton } from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalSalesByZipCodeChart/TotalSalesByZipCodeChartSkeleton";
import TotalSalesNewCustomerByZipCodeAndYearChart from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalSalesNewCustomerByZipCodeAndYearChart/TotalSalesNewCustomerByZipCodeAndYearChart";
import TotalSalesNewCustomerByZipCodeAndYearChartSkeleton from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalSalesNewCustomerByZipCodeAndYearChart/TotalSalesNewCustomerByZipCodeAndYearChartSkeleton";
import RevenueByInvoiceAgeChart from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/RevenueByInvoiceAgeChart/RevenueByInvoiceAgeChart";
import RevenueByInvoiceAgeChartSkeleton from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/RevenueByInvoiceAgeChart/RevenueByInvoiceAgeChartSkeleton";
import LifetimeValueByTierChart from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/LifetimeValueByTierChart/LifetimeValueByTierChart";
import LifetimeValueByTierChartSkeleton from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/LifetimeValueByTierChart/LifetimeValueByTierChartSkeleton";
import { LifetimeValueByTierTable } from "@/modules/stochastic/features/Reports/SCDashboard/components/Table/LifetimeValueByTierTable/LifetimeValueByTierTable";
import { LifetimeValueByTierTableSkeleton } from "@/modules/stochastic/features/Reports/SCDashboard/components/Table/LifetimeValueByTierTable/LifetimeValueByTierTableSkeleton";
import { PercentageOfNewCustomersByZipCodeChart } from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/PercentageOfNewCustomersByZipCodeChart/PercentageOfNewCustomersByZipCodeChart";
import { PercentageOfNewCustomersByZipCodeChartSkeleton } from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/PercentageOfNewCustomersByZipCodeChart/PercentageOfNewCustomersByZipCodeChartSkeleton";
import { PercentageOfNewCustomersChangeByZipCodeTable } from "@/modules/stochastic/features/Reports/SCDashboard/components/Table/PercentageOfNewCustomersChangeByZipCodeTable/PercentageOfNewCustomersChangeByZipCodeTable";
import { PercentageOfNewCustomersChangeByZipCodeTableSkeleton } from "@/modules/stochastic/features/Reports/SCDashboard/components/Table/PercentageOfNewCustomersChangeByZipCodeTable/PercentageOfNewCustomersChangeByZipCodeTableSkeleton";
import CustomersAverageInvoiceComparisonChart from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/CustomersAverageInvoiceComparisonChart/CustomersAverageInvoiceComparisonChart";
import { CustomersAverageInvoiceComparisonChartSkeleton } from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/CustomersAverageInvoiceComparisonChart/CustomersAverageInvoiceComparisonChartSkeleton";
import NewCustomerReplacementEfficiencyChart from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/NewCustomerReplacementEfficiencyChart/NewCustomerReplacementEfficiencyChart";
import NewCustomerReplacementEfficiencyChartSkeleton from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/NewCustomerReplacementEfficiencyChart/NewCustomerReplacementEfficiencyChartSkeleton";
import ExistingCustomerReplacementEfficiencyChart from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/ExistingCustomerReplacementEfficiencyChart/ExistingCustomerReplacementEfficiencyChart";
import ExistingCustomerReplacementEfficiencyChartSkeleton from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/ExistingCustomerReplacementEfficiencyChart/ExistingCustomerReplacementEfficiencyChartSkeleton";
import TotalHouseholdsByZipCodeChart from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalHouseholdsByZipCodeChart/TotalHouseholdsByZipCodeChart";
import { TotalHouseholdsByZipCodeChartSkeleton } from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalHouseholdsByZipCodeChart/TotalHouseholdsByZipCodeChartSkeleton";

type DashboardData = StochasticDashboardState["dashboardData"];

export type DashboardTab = "sales" | "customers" | "review";

/**
 * Chart keys mirror the backend DashboardChartCatalog — the per-stage
 * config API speaks these ids, and they double as the scroll-anchor DOM
 * ids the ChartIndex targets (#9292).
 */
export const TAB_CHART_ORDER: Record<DashboardTab, string[]> = {
  sales: [
    "total-sales-new-vs-existing",
    "total-sales-by-year-month",
    "total-sales-by-zip-code",
    "total-sales-new-customer-by-zip",
  ],
  customers: [
    "revenue-by-invoice-age",
    "lifetime-value-by-tier",
    "lifetime-value-by-tier-table",
    "pct-new-customers-by-zip",
    "pct-new-customers-change-table",
    "avg-invoice-comparison",
    "new-customer-replacement",
    "existing-customer-replacement",
  ],
  review: [
    "total-sales-by-year-month",
    "revenue-by-invoice-age",
    "total-sales-by-zip-code",
    "total-households-by-zip",
    "pct-new-customers-by-zip",
    "avg-invoice-comparison",
    "new-customer-replacement",
    "existing-customer-replacement",
    "lifetime-value-by-tier",
  ],
};

/**
 * The charts a tab should show, honoring the stage config: only allowed
 * keys survive, ordered by the config's order (per-stage curation wins
 * over the tab's historical order). No config (null) = the tab's full
 * historical list — the ungated fail-open default.
 */
export function orderChartsForTab(
  tab: DashboardTab,
  allowedOrderedKeys: string[] | null,
): string[] {
  const tabKeys = TAB_CHART_ORDER[tab];
  if (allowedOrderedKeys === null) return tabKeys;
  return allowedOrderedKeys.filter((key) => tabKeys.includes(key));
}

export function renderChart(
  key: string,
  dashboardData: DashboardData,
): React.ReactNode {
  switch (key) {
    case "total-sales-new-vs-existing":
      return (
        <TotalSalesNewVsExistingCustomerChart
          initialData={dashboardData.totalSalesNewVsExistingCustomerData || []}
        />
      );
    case "total-sales-by-year-month":
      return (
        <TotalSalesByYearAndMonthChart
          initialData={dashboardData.totalSalesByYearAndMonthData}
        />
      );
    case "total-sales-by-zip-code":
      return (
        <TotalSalesByZipCodeChart
          initialData={dashboardData.totalSalesByZipCodeData || []}
        />
      );
    case "total-sales-new-customer-by-zip":
      return (
        <TotalSalesNewCustomerByZipCodeAndYearChart
          initialData={dashboardData.totalSalesNewCustomerByZipCodeAndYearData}
        />
      );
    case "revenue-by-invoice-age":
      return (
        <RevenueByInvoiceAgeChart
          initialData={dashboardData.revenueByInvoiceAgeData || []}
        />
      );
    case "lifetime-value-by-tier":
      return (
        <LifetimeValueByTierChart
          initialData={dashboardData.lifetimeValueByTierData}
        />
      );
    case "lifetime-value-by-tier-table":
      return (
        <LifetimeValueByTierTable
          initialData={dashboardData.lifetimeValueByTierData}
        />
      );
    case "pct-new-customers-by-zip":
      return (
        <PercentageOfNewCustomersByZipCodeChart
          initialData={dashboardData.percentageOfNewCustomersByZipCodeData}
        />
      );
    case "pct-new-customers-change-table":
      return (
        <PercentageOfNewCustomersChangeByZipCodeTable
          initialData={
            dashboardData.percentageOfNewCustomersChangeByZipCodeData || []
          }
        />
      );
    case "avg-invoice-comparison":
      return (
        <CustomersAverageInvoiceComparisonChart
          initialData={
            dashboardData.customersAverageInvoiceComparisonData || []
          }
        />
      );
    case "new-customer-replacement":
      return (
        <NewCustomerReplacementEfficiencyChart
          initialData={dashboardData.newCustomerReplacementEfficiencyData || []}
          monthlyData={
            dashboardData.newCustomerReplacementEfficiencyByMonthData
          }
        />
      );
    case "existing-customer-replacement":
      return (
        <ExistingCustomerReplacementEfficiencyChart
          initialData={
            dashboardData.existingCustomerReplacementEfficiencyData || []
          }
          monthlyData={
            dashboardData.existingCustomerReplacementEfficiencyByMonthData
          }
        />
      );
    case "total-households-by-zip":
      return (
        <TotalHouseholdsByZipCodeChart
          initialData={dashboardData.totalHouseholdsByZipCodeData}
        />
      );
    default:
      return null;
  }
}

const CHART_SKELETONS: Record<string, React.ComponentType> = {
  "total-sales-new-vs-existing": TotalSalesNewVsExistingCustomerChartSkeleton,
  "total-sales-by-year-month": TotalSalesByYearAndMonthChartSkeleton,
  "total-sales-by-zip-code": TotalSalesByZipCodeChartSkeleton,
  "total-sales-new-customer-by-zip":
    TotalSalesNewCustomerByZipCodeAndYearChartSkeleton,
  "revenue-by-invoice-age": RevenueByInvoiceAgeChartSkeleton,
  "lifetime-value-by-tier": LifetimeValueByTierChartSkeleton,
  "lifetime-value-by-tier-table": LifetimeValueByTierTableSkeleton,
  "pct-new-customers-by-zip": PercentageOfNewCustomersByZipCodeChartSkeleton,
  "pct-new-customers-change-table":
    PercentageOfNewCustomersChangeByZipCodeTableSkeleton,
  "avg-invoice-comparison": CustomersAverageInvoiceComparisonChartSkeleton,
  "new-customer-replacement": NewCustomerReplacementEfficiencyChartSkeleton,
  "existing-customer-replacement":
    ExistingCustomerReplacementEfficiencyChartSkeleton,
  "total-households-by-zip": TotalHouseholdsByZipCodeChartSkeleton,
};

export function renderChartSkeleton(key: string): React.ReactNode {
  const Skeleton = CHART_SKELETONS[key];
  return Skeleton ? <Skeleton /> : null;
}
